import { Check, ShieldCheck, ArrowRight } from "lucide-react";
import { Link } from "react-router-dom";
import { Reveal, Eyebrow, Chip } from "@/components/Reveal";
import CheckoutButton from "@/components/CheckoutButton";
import SamplePreviewButton from "@/components/SamplePreviewModal";

const MAY_INCLUDE = [
  "Google Business Profile review",
  "Website and mobile experience review",
  "Search visibility observations",
  "Local SEO review",
  "Social-media presence review",
  "Brand consistency review",
  "Lead-generation opportunities",
  "Conversion-path review",
  "Competitor observations",
  "Marketing technology review",
  "Recommended next actions",
];

const LADDER = ["$9.99 Snapshot", "$99.99 Assessment", "Build", "Grow", "Scale"];

const TIERS = [
  {
    testid: "offer-buy-express",
    lookup: "aih_express_snapshot",
    tag: "Automated · Low-Friction Entry",
    name: "Business Snapshot",
    price: "$9.99",
    desc: "Automated preliminary review with a short AI-generated summary.",
    includes: [
      "Website scan",
      "Basic SEO observations",
      "Google presence check",
      "Social presence check",
      "Obvious conversion issues",
      "Short AI-generated summary",
    ],
    flow: "Purchase → Business intake (name + email) → Automated review → Snapshot published to your portal, typically within minutes",
    cta: "Get My Snapshot — $9.99",
    featured: false,
  },
  {
    testid: "offer-buy-full",
    lookup: "aih_full_assessment",
    tag: "Human-Reviewed · Recommended",
    name: "Business Growth Assessment",
    price: "$99.99",
    desc: "Human-reviewed assessment, competitor observations, prioritized recommendations, one-on-one strategy session, and implementation roadmap.",
    includes: [
      "Comprehensive assessment of your digital footprint",
      "Competitor observations",
      "Prioritized recommendations",
      "One-on-one strategy session — booked right after intake",
      "Implementation roadmap",
    ],
    flow: "Purchase → Business intake → Book your strategy session → Human-reviewed report published to your portal",
    cta: "Get My Assessment — $99.99",
    featured: true,
  },
];

export const AssessmentOffers = () => (
  <section id="assessment" className="relative py-24 sm:py-32">
    <div className="max-w-7xl mx-auto px-5 sm:px-8">
      <Reveal>
        <Eyebrow>The Front Door</Eyebrow>
        <h2 className="font-display text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight text-white mt-4 max-w-3xl">
          Start With the Business Growth Assessment
        </h2>
        <p className="text-slate-400 mt-4 max-w-2xl leading-relaxed">
          One-time fee. Optional next steps only — purchasing an assessment never obligates you to
          hire All In Hand Marketing afterward.
        </p>
      </Reveal>

      <div className="grid lg:grid-cols-2 gap-6 mt-14 items-stretch">
        {TIERS.map((t, i) => (
          <Reveal key={t.lookup} delay={i * 0.12} className="h-full">
            <div
              className={`relative h-full rounded-3xl p-8 sm:p-10 flex flex-col transition-transform duration-500 hover:-translate-y-1 ${
                t.featured
                  ? "bg-obsidian-800 border border-gold/40 gold-ring"
                  : "bg-obsidian-800/60 border border-slate-700/60 hover:border-emerald-500/40"
              }`}
            >
              {t.featured && (
                <span className="absolute -top-3 left-8 rounded-full bg-gold text-obsidian font-mono text-[10px] font-semibold tracking-[0.2em] uppercase px-4 py-1.5">
                  Recommended
                </span>
              )}
              <p
                className={`font-mono text-[11px] uppercase tracking-[0.3em] ${
                  t.featured ? "text-gold" : "text-emerald-400"
                }`}
              >
                {t.tag}
              </p>
              <h3 className="font-display text-2xl sm:text-3xl font-bold text-white mt-3">{t.name}</h3>
              <p className="text-slate-300 mt-3 leading-relaxed text-sm sm:text-base">{t.desc}</p>
              <div className="flex items-end gap-2 mt-7">
                <span className="font-display text-5xl font-extrabold text-white">{t.price}</span>
                <span className="text-slate-400 text-sm mb-1.5">one-time</span>
              </div>
              <div className="mt-6 rounded-2xl border border-slate-700/60 bg-obsidian-900/50 px-4 py-3.5" data-testid={`${t.testid}-fulfillment`}>
                <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-emerald-400 mb-1.5">
                  How fulfillment works
                </p>
                <p className="text-slate-300 text-xs leading-relaxed flex gap-2 items-start">
                  <ArrowRight className="w-3.5 h-3.5 shrink-0 mt-0.5 text-gold" />
                  {t.flow}
                </p>
              </div>
              <ul className="mt-6 space-y-3 flex-1">
                {t.includes.map((f) => (
                  <li key={f} className="flex gap-3 text-sm text-slate-300">
                    <Check className={`w-4 h-4 mt-0.5 shrink-0 ${t.featured ? "text-gold" : "text-emerald-400"}`} />
                    <span>{f}</span>
                  </li>
                ))}
              </ul>
              <div className="mt-8 flex flex-col gap-3">
                <CheckoutButton
                  lookup={t.lookup}
                  label={t.cta}
                  testid={t.testid}
                  className={`w-full rounded-full py-4 font-display font-bold transition-all duration-300 disabled:opacity-60 disabled:cursor-wait ${
                    t.featured
                      ? "bg-gold text-obsidian hover:bg-gold-bright hover:-translate-y-0.5"
                      : "border border-emerald-500/60 text-emerald-400 hover:bg-emerald-500/10"
                  }`}
                />
                {t.featured && (
                  <SamplePreviewButton
                    testid="offers-preview-sample"
                    className="w-full rounded-full border border-slate-500 text-slate-200 font-semibold py-3 text-sm hover:border-gold hover:text-gold transition-colors"
                  />
                )}
                <p className="text-slate-400 text-xs leading-relaxed flex gap-2" data-testid={`${t.testid}-eligibility`}>
                  <ShieldCheck className="w-3.5 h-3.5 text-gold shrink-0 mt-0.5" />
                  <span>
                    7-Day Money-Back Guarantee: refund requests within 7 calendar days if this offer
                    is guarantee-eligible at checkout — conditions and exclusions apply (for example,
                    work already delivered). See the{" "}
                    <Link to="/refund-policy" className="text-gold underline underline-offset-2">
                      Refund Policy
                    </Link>
                    .
                  </span>
                </p>
              </div>
            </div>
          </Reveal>
        ))}
      </div>

      <Reveal delay={0.1} className="mt-10">
        <div className="rounded-2xl border border-slate-800 bg-obsidian-900/50 px-6 py-5 flex flex-wrap items-center justify-center gap-x-3 gap-y-2">
          {LADDER.map((step, i) => (
            <span key={step} className="flex items-center gap-3">
              <span className={`font-display font-bold text-sm ${i === 0 ? "text-emerald-400" : "text-slate-300"}`}>
                {step}
              </span>
              {i < LADDER.length - 1 && <ArrowRight className="w-3.5 h-3.5 text-gold/60" />}
            </span>
          ))}
        </div>
      </Reveal>

      <Reveal delay={0.12} className="mt-6">
        <div className="rounded-2xl border border-gold/25 bg-obsidian-800/50 p-6 flex flex-col sm:flex-row sm:items-center gap-5 justify-between">
          <div className="flex gap-4">
            <ShieldCheck className="w-6 h-6 text-gold shrink-0 mt-0.5" />
            <p className="text-sm text-slate-300 leading-relaxed max-w-xl">
              <span className="text-gold font-semibold">7-Day Guarantee.</span> Eligible purchases
              are covered by our 7-day money-back guarantee, subject to our{" "}
              <Link to="/refund-policy" data-testid="offers-refund-link" className="text-gold underline underline-offset-2">
                Refund Policy
              </Link>
              . Eligibility and exclusions apply.
            </p>
          </div>
          <div className="flex flex-wrap gap-x-4 gap-y-1 text-[11px] text-slate-400 shrink-0">
            <span className="inline-flex items-center gap-1.5">Secure Checkout</span>
            <span className="inline-flex items-center gap-1.5">Clear Scope</span>
            <span className="inline-flex items-center gap-1.5">Human Support</span>
          </div>
        </div>
      </Reveal>

      <Reveal delay={0.1} className="mt-12">
        <p className="font-mono text-[11px] uppercase tracking-[0.3em] text-slate-400 mb-5">
          Your full assessment may include
        </p>
        <div className="flex flex-wrap gap-2.5">
          {MAY_INCLUDE.map((c) => (
            <Chip key={c}>{c}</Chip>
          ))}
        </div>
      </Reveal>
    </div>
  </section>
);

export default AssessmentOffers;
